import { useEffect, useMemo, useState } from 'react'

import { isPdfUrl, renderPdfPageToDataUrl } from 'shared/helpers/pdf'

type UsePdfPreviewImageOptions = {
  pageNumber?: number
  scale?: number
}

export const usePdfPreviewImage = (
  url: string | null | undefined,
  { pageNumber = 1, scale = 1.5 }: UsePdfPreviewImageOptions = {}
) => {
  const normalizedUrl = useMemo(() => {
    if (!url) {
      return ''
    }
    return url.replace('type=medium', 'type=highest')
  }, [url])
  const isPdf = useMemo(
    () => Boolean(normalizedUrl) && isPdfUrl(normalizedUrl),
    [normalizedUrl]
  )
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!normalizedUrl) {
      setPreviewUrl(null)
      setError(null)
      return
    }

    if (!isPdf) {
      setPreviewUrl(normalizedUrl)
      setError(null)
      return
    }

    let canceled = false
    setIsLoading(true)

    renderPdfPageToDataUrl({
      pdfUrl: normalizedUrl,
      pageNumber,
      scale
    })
      .then(dataUrl => {
        if (!canceled) {
          setPreviewUrl(dataUrl)
          setError(null)
        }
      })
      .catch(err => {
        if (!canceled) {
          setPreviewUrl(null)
          setError(err as Error)
        }
      })
      .finally(() => {
        if (!canceled) {
          setIsLoading(false)
        }
      })

    return () => {
      canceled = true
    }
  }, [normalizedUrl, isPdf, pageNumber, scale])

  return { previewUrl, isPdf, isLoading, error }
}
